/**
 * Tab 2 of the "Данные" page — label2-style crop annotation grid (superuser).
 *
 * All crops render at once as a thumbnail grid (lazy <img>); a focus cursor
 * moves with the arrow keys and after each label. One keypress labels the
 * focused crop — no per-image waiting, no view switching. Two modes map to the
 * two ResNet models of the cascade:
 *   • Режим 1 — occupancy (model 1, binary): Q = занято, W = свободно; both set
 *     the label and auto-advance. Shows ALL crops.
 *   • Режим 2 — table state (model 2, 4 independent flags): Q/W/E/R toggle the
 *     flags, Space/Enter advances. Shows ONLY crops with occupied=false (zone
 *     empty) — the same gate the inference pipeline uses.
 *
 * The local annotations.json (server-side working copy) is the source of truth:
 * edits debounce-save into it via annotateBulk; "Залить в ClearML" ships it as a
 * new version (keeping the local copy); "Удалить локальную копию" frees it.
 */
import { Select } from '@/components/ui/select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { Loader2, UploadCloud, Trash2, CheckCheck, ZoomIn, ZoomOut } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { API_BASE } from '@/api/base';
import {
  getDownload, getCrops, annotateBulk, uploadNewVersion, deleteLocalCopy, deleteCrop,
  type CropItem, type CropLabelDelta,
} from '@/api/datasets';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { layoutSafeLetter } from '@/utils/keyboardLayout';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tooltip } from '@/components/ui/tooltip';

const MIN_COLS = 2;
const MAX_COLS = 8;

// Conversion funnel: model 1 (people/items) → model 2 (table in frame / moved)
// → model 3 (table state). Each stage only labels crops the earlier one passed.
type Mode = 'presence' | 'in_frame' | 'state';
const MODES: Mode[] = ['presence', 'in_frame', 'state'];
// Binary modes label a single field with Q (true) / W (false) + auto-advance.
const BINARY_FIELD: Partial<Record<Mode, string>> = { presence: 'occupied', in_frame: 'table_in_frame' };

const STATE_KEYS: { field: string; key: string; label: string }[] = [
  { field: 'native_objects_table', key: 'q', label: 'Нативные объекты' },
  { field: 'personal_items_table', key: 'w', label: 'Личные вещи на столе' },
  { field: 'food_dishes_table', key: 'e', label: 'Посуда с едой' },
  { field: 'dirty_dishes_table', key: 'r', label: 'Грязная посуда/мусор' },
];
type Labels = Record<string, boolean>; // field -> bool, per crop
const keyOf = (it: { table_id: number; frame_id: number }) => `${it.table_id}:${it.frame_id}`;
// Keep only the label heads a human actually set — drop null/undefined so an unset
// field stays absent (undefined), never a spurious false. Used to resume pending crops.
const onlySet = (o: Record<string, boolean | null>): Labels =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v === true || v === false)) as Labels;

interface Props { downloadId: number; onClose?: () => void; }

export default function CropAnnotator({ downloadId, onClose }: Props) {
  const { t } = useT();
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<Mode>('presence');
  const [src, setSrc] = useState<'all' | 'human' | 'ai' | 'unlabeled'>('all');
  // Object filter — narrow the grid to a single table (work through one object's
  // frames pointwise). 'all' = every table in the dataset.
  const [tableFilter, setTableFilter] = useState<number | 'all'>('all');
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [deletedKeys, setDeletedKeys] = useState<Set<string>>(new Set());
  // «Неразмеченные» cohort — FROZEN at load (crops that arrived source='pending-human').
  // Membership never shrinks in-session, so a crop you just labelled STAYS in the filter
  // until you reload the page → you can fix a misclick right there.
  const initialUnlabeled = useRef<Set<string>>(new Set());
  // Crops whose STATE stage a human reviewed this session (toggled a flag OR pressed
  // Space in state mode). An all-false state is a valid label, so "state done" can't be
  // inferred from the values — this explicit marker completes the funnel.
  const [stateReviewed, setStateReviewed] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState(0);
  const [saved, setSaved] = useState('');
  const [cols, setCols] = useState(3);
  const [desc, setDesc] = useState('');
  // Each later model depends on the earlier one (it only labels crops the earlier
  // model passed). Switching to it first shows a one-time gate explaining that.
  const [gateFor, setGateFor] = useState<Mode | null>(null);
  const [acked, setAcked] = useState<Set<Mode>>(new Set(['presence']));

  const requestMode = useCallback((m: Mode) => {
    if (m === mode) return;
    if (!acked.has(m)) { setGateFor(m); return; }
    setMode(m);
  }, [mode, acked]);

  // 1. Wait for the download to be ready.
  const { data: dl } = useSWR(
    ['mml-download', downloadId],
    () => getDownload(downloadId).then(r => r.data),
    { refreshInterval: (d) => (d?.status === 'ready' || d?.status === 'failed' ? 0 : 2000) },
  );
  const ready = dl?.status === 'ready';

  // 2. Load ALL crops once when ready.
  const { data: cropsResp } = useSWR(
    ready ? ['mml-crops', downloadId] : null,
    () => getCrops(downloadId).then(r => r.data),
    { revalidateOnFocus: false },
  );

  // 3. Local label state (source of truth for the session; debounce-saved).
  const [labels, setLabels] = useState<Record<string, Labels>>({});
  useEffect(() => {
    if (!cropsResp) return;
    const init: Record<string, Labels> = {};
    const unl = new Set<string>();
    for (const it of cropsResp.items) {
      const k = keyOf(it);
      if (it.source === 'pending-human') {
        // Still in the from-scratch queue. Resume partial progress: seed ONLY the label
        // heads a human actually set (API sends null for unset), so occupied/in_frame
        // stay undefined until explicitly labelled — the funnel then treats "unlabeled"
        // as distinct from "free"/"out-of-frame". A multi-session labeller picks up
        // exactly where they left off across reloads.
        unl.add(k);
        init[k] = onlySet({ ...it.judgement, ...it.presence, ...it.state });
      } else {
        init[k] = { ...it.judgement, ...it.presence, ...it.state } as Labels;
      }
    }
    initialUnlabeled.current = unl;
    setLabels(init);
    setTouched(new Set());
    setStateReviewed(new Set());
  }, [cropsResp]);

  const items: CropItem[] = useMemo(() => cropsResp?.items ?? [], [cropsResp]);

  // Three annotation states of a crop:
  //   • human     — a person labeled it (source='user') or edited it this session.
  //   • unlabeled — neither human nor AI touched it: our collector published it with
  //                 source='pending-human' and NO label truth (a from-scratch queue).
  //   • ai        — has labels but not from a human (bootstrap Claude-vision; legacy
  //                 records carry no explicit source).
  const stateOf = useCallback(
    (it: CropItem): 'human' | 'ai' | 'unlabeled' => {
      if (it.source === 'user' || touched.has(keyOf(it))) return 'human';
      if (it.source === 'pending-human') return 'unlabeled';
      return 'ai';
    },
    [touched],
  );

  // Facet bucket for the source filter + its counts. The frozen «Неразмеченные» cohort
  // wins (sticky: a crop you label this session stays in that bucket until reload), else
  // the crop's annotation state. Buckets are DISJOINT (unlabeled / human / ai) so the
  // four chip counts sum to the total and every facet number lines up.
  const bucketOf = useCallback(
    (it: CropItem): 'human' | 'ai' | 'unlabeled' =>
      (initialUnlabeled.current.has(keyOf(it)) ? 'unlabeled' : stateOf(it)),
    [stateOf],
  );

  // Distinct tables (object filter), each counted WITHIN the current source filter — so
  // e.g. under «Неразмеченные» each table shows how many of ITS crops are still unlabeled,
  // and Σ per-table = the «Неразмеченные» chip count. Sorted numeric-aware.
  const tableOptions = useMemo(() => {
    const m = new Map<number, { id: number; name: string; count: number }>();
    for (const it of items) {
      if (deletedKeys.has(keyOf(it))) continue;
      const b = bucketOf(it);
      const inSrc = src === 'all' ? b !== 'unlabeled' : b === src;
      if (!inSrc) continue;
      const e = m.get(it.table_id);
      if (e) e.count++;
      else m.set(it.table_id, { id: it.table_id, name: it.table ?? `t${it.table_id}`, count: 1 });
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [items, deletedKeys, src, bucketOf]);

  // Funnel (live from local labels): presence=all; in_frame=free crops
  // (occupied=false); state=free AND in-frame. The source filter then narrows by
  // annotation state — «Все (размеченные)» = human ∪ ai (excludes the unlabeled
  // queue), «Неразмеченные» = only the from-scratch crops.
  const visible = useMemo(() => {
    let list = items.filter(it => !deletedKeys.has(keyOf(it)));
    if (tableFilter !== 'all') list = list.filter(it => it.table_id === tableFilter);
    // Funnel by EXPLICIT labels (unlabeled crops have occupied/in_frame === undefined,
    // so they only enter stage 2/3 once a human marks them free / in-frame):
    //   occupied=true  → done, never reaches stage 2/3;
    //   occupied=false → stage 2 (in frame?); in_frame=true → stage 3 (state).
    if (mode === 'in_frame') list = list.filter(it => labels[keyOf(it)]?.occupied === false);
    else if (mode === 'state') list = list.filter(it => {
      const l = labels[keyOf(it)];
      return l?.occupied === false && l?.table_in_frame === true;
    });
    // «Неразмеченные» = the frozen cohort (sticky: a crop you label this session stays
    // here until reload). «Все (размеченные)» excludes it; human/ai narrow by state.
    if (src === 'all') list = list.filter(it => bucketOf(it) !== 'unlabeled');
    else list = list.filter(it => bucketOf(it) === src);
    return list;
  }, [items, mode, labels, src, bucketOf, deletedKeys, tableFilter]);

  // Source-facet chip counts, respecting the current TABLE filter (so with a table picked
  // the four chips show that table's breakdown; with «Все столы» they show the whole set).
  // Disjoint buckets → all+... note: `all` = human ∪ ai (labelled, out of the queue).
  const srcCounts = useMemo(() => {
    const c: Record<'all' | 'human' | 'ai' | 'unlabeled', number> = { all: 0, human: 0, ai: 0, unlabeled: 0 };
    for (const it of items) {
      if (deletedKeys.has(keyOf(it))) continue;
      if (tableFilter !== 'all' && it.table_id !== tableFilter) continue;
      const b = bucketOf(it);
      c[b] += 1;
      if (b !== 'unlabeled') c.all += 1;
    }
    return c;
  }, [items, deletedKeys, tableFilter, bucketOf]);

  // For the «Неразмеченные» filter: how many crops in the CURRENT view (cohort ∩ table)
  // still haven't reached a funnel terminal — the "осталось" progress number.
  const remainingInView = useMemo(() => {
    if (src !== 'unlabeled') return null;
    let n = 0;
    for (const it of items) {
      const k = keyOf(it);
      if (!initialUnlabeled.current.has(k) || deletedKeys.has(k)) continue;
      if (tableFilter !== 'all' && it.table_id !== tableFilter) continue;
      const l = labels[k] || {};
      const done = l.occupied === true
        || (l.occupied === false && l.table_in_frame === false)
        || (l.occupied === false && l.table_in_frame === true && stateReviewed.has(k));
      if (!done) n++;
    }
    return n;
  }, [src, items, labels, stateReviewed, deletedKeys, tableFilter]);

  // Keep focus in range when the visible set changes (e.g. mode switch).
  useEffect(() => { setFocus(f => Math.min(f, Math.max(0, visible.length - 1))); }, [visible.length]);

  // ── debounced bulk save ────────────────────────────────────────────────────
  const dirty = useRef<Set<string>>(new Set());
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  const stateReviewedRef = useRef(stateReviewed);
  stateReviewedRef.current = stateReviewed;
  // A from-scratch crop has completed its funnel (→ leaves «Неразмеченные» on reload)
  // when: it's occupied; or free & out-of-frame; or free & in-frame & state-reviewed.
  const funnelDone = useCallback((k: string) => {
    const l = labelsRef.current[k] || {};
    if (l.occupied === true) return true;
    if (l.occupied === false && l.table_in_frame === false) return true;
    if (l.occupied === false && l.table_in_frame === true && stateReviewedRef.current.has(k)) return true;
    return false;
  }, []);
  const itemByKey = useMemo(() => {
    const m: Record<string, CropItem> = {};
    for (const it of items) m[keyOf(it)] = it;
    return m;
  }, [items]);
  const saveTimer = useRef<number | null>(null);

  const flush = useCallback(async () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (!dirty.current.size) return;
    const keys = [...dirty.current];
    dirty.current.clear();
    const toDelta = (k: string): CropLabelDelta => {
      const it = itemByKey[k];
      return { table_id: it.table_id, frame_id: it.frame_id, ...labelsRef.current[k] } as CropLabelDelta;
    };
    // From-scratch crops persist as source='pending-human' (stay in «Неразмеченные»
    // after reload) UNTIL their funnel is complete; everything else persists as a
    // human label. This is what makes a fully-labelled crop leave the queue on reload
    // while a half-labelled one comes back to be finished. The trainer drops
    // source='pending-human' rows, so an in-progress crop never reaches training.
    const pending = keys.filter(k => initialUnlabeled.current.has(k) && !funnelDone(k));
    const asUser = keys.filter(k => !pending.includes(k));
    try {
      let saved = 0;
      if (asUser.length) {
        const r = await annotateBulk(downloadId, asUser.map(toDelta), 'user');
        saved += r.data.saved;
      }
      if (pending.length) {
        const r = await annotateBulk(downloadId, pending.map(toDelta), 'pending-human');
        saved += r.data.saved;
      }
      setSaved(`✓ ${saved}`);
      window.setTimeout(() => setSaved(s => (s.startsWith('✓') ? '' : s)), 1500);
    } catch {
      setSaved('save failed');
      keys.forEach(k => dirty.current.add(k)); // retry next flush
    }
  }, [downloadId, itemByKey, funnelDone]);

  const scheduleSave = useCallback((k: string) => {
    dirty.current.add(k);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(flush, 250);
  }, [flush]);

  // Flush pending edits on unmount.
  useEffect(() => () => { void flush(); }, [flush]);

  // ── label mutations ────────────────────────────────────────────────────────
  const setField = useCallback((it: CropItem, field: string, value: boolean) => {
    const k = keyOf(it);
    setLabels(prev => ({ ...prev, [k]: { ...prev[k], [field]: value } }));
    setTouched(prev => (prev.has(k) ? prev : new Set(prev).add(k)));
    scheduleSave(k);
  }, [scheduleSave]);

  // Mark the state stage reviewed for a crop (completes its funnel). Called on a flag
  // toggle OR Space in state mode — so an all-false clean table also counts as done
  // and persists (source flips to 'user' → leaves «Неразмеченные» on next reload).
  const markStateReviewed = useCallback((it: CropItem) => {
    const kk = keyOf(it);
    setStateReviewed(prev => (prev.has(kk) ? prev : new Set(prev).add(kk)));
    setTouched(prev => (prev.has(kk) ? prev : new Set(prev).add(kk)));
    scheduleSave(kk);
  }, [scheduleSave]);

  const advance = useCallback(() => {
    setFocus(f => Math.min(f + 1, Math.max(0, visible.length - 1)));
  }, [visible.length]);

  const move = useCallback((dir: 'left' | 'right' | 'up' | 'down') => {
    setFocus(f => {
      const n = visible.length;
      if (!n) return 0;
      if (dir === 'right') return Math.min(n - 1, f + 1);
      if (dir === 'left') return Math.max(0, f - 1);
      if (dir === 'down') return Math.min(n - 1, f + cols);
      return Math.max(0, f - cols);
    });
  }, [visible.length, cols]);

  // ── keyboard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack keys while typing in the description field / any input.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      // Physical key, not the character — layout-independent (utils/keyboardLayout).
      const k = layoutSafeLetter(e);
      if (e.key === 'ArrowRight') { e.preventDefault(); move('right'); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); move('left'); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); move('down'); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); move('up'); return; }
      if (e.key === 'Tab') { e.preventDefault(); requestMode(MODES[(MODES.indexOf(mode) + 1) % MODES.length]); return; }
      const it = visible[focus];
      if (!it) return;
      const binaryField = BINARY_FIELD[mode];
      if (binaryField) {
        if (k === 'q') { e.preventDefault(); setField(it, binaryField, true); advance(); }
        else if (k === 'w') { e.preventDefault(); setField(it, binaryField, false); advance(); }
      } else {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); markStateReviewed(it); advance(); return; }
        const hit = STATE_KEYS.find(s => s.key === k);
        if (hit) { e.preventDefault(); markStateReviewed(it); setField(it, hit.field, !labelsRef.current[keyOf(it)]?.[hit.field]); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mode, focus, visible, move, advance, setField, requestMode, markStateReviewed]);

  // ── keep the focused row centered as the cursor moves ──────────────────────
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    cardRefs.current[focus]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focus, visible]);

  // ── actions ────────────────────────────────────────────────────────────────
  const upload = async () => {
    await flush();
    if (!confirm(t('data.annotate.uploadConfirm'))) return;
    setSubmitting(true);
    try { await uploadNewVersion(downloadId, desc.trim()); setSaved(t('data.annotate.uploaded')); }
    finally { setSubmitting(false); }
  };
  // Confirm the current labels (no change) as human-reviewed: persists source=
  // 'user' into annotations.json so these crops leave the "AI" filter next time.
  const markReviewed = async () => {
    const list = visible;
    if (!list.length) return;
    if (!confirm(t('data.annotate.reviewConfirm').replace('{n}', String(list.length)))) return;
    setSubmitting(true);
    try {
      await flush(); // land any pending label edits first
      const deltas = list.map(it => ({ table_id: it.table_id, frame_id: it.frame_id }) as CropLabelDelta);
      await annotateBulk(downloadId, deltas, 'user');
      setTouched(prev => {
        const n = new Set(prev);
        list.forEach(it => n.add(keyOf(it)));
        return n;
      });
      setSaved(t('data.annotate.reviewedDone').replace('{n}', String(list.length)));
    } finally { setSubmitting(false); }
  };

  const deletePhoto = async (it: CropItem) => {
    if (!confirm(t('data.annotate.deletePhotoConfirm'))) return;
    try {
      await deleteCrop(downloadId, it.table_id, it.frame_id);
      setDeletedKeys(prev => new Set(prev).add(keyOf(it)));
      setSaved(t('data.annotate.photoDeleted'));
    } catch { setSaved('delete failed'); }
  };

  const remove = async () => {
    if (!confirm(t('data.annotate.deleteConfirm'))) return;
    setSubmitting(true);
    try {
      await deleteLocalCopy(downloadId);
      // Refresh the hierarchy + active-downloads so the deleted copy stops
      // showing as a live "[ready] open".
      await Promise.all([
        globalMutate('mml-hierarchy'),
        globalMutate('mml-active-downloads'),
      ]);
      onClose?.();
    } finally { setSubmitting(false); }
  };

  if (!dl) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  if (dl.status === 'failed')
    return <div className="text-sm text-destructive">{t('data.annotate.failed')}: {dl.status_detail}</div>;
  if (!ready) {
    const elapsed = dl.downloaded_at
      ? Math.max(0, Math.round((Date.now() - new Date(dl.downloaded_at).getTime()) / 1000))
      : 0;
    return (
      <div className="mx-auto mt-10 max-w-md space-y-2 rounded-md border p-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('data.annotate.downloading')}
        </div>
        <div className="relative h-2 w-full overflow-hidden rounded bg-muted">
          <div className="progress-indeterminate bg-primary" />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{dl.status_detail || `${dl.status}…`}</span>
          <span>{elapsed}s</span>
        </div>
      </div>
    );
  }
  if (!cropsResp) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  if (!cropsResp.total) return <div className="text-sm text-muted-foreground">{t('data.annotate.noCrops')}</div>;

  return (
    <div className="space-y-3">
      {/* toolbar: mode toggle + progress + actions */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-md border bg-background/95 px-3 py-2 backdrop-blur">
        <div className="inline-flex overflow-hidden rounded-md border">
          {MODES.map((m, idx) => (
            <button
              key={m}
              className={`px-3 py-1 text-sm ${mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              onClick={() => requestMode(m)}
            >
              {idx + 1}. {t(`data.annotate.mode.${m}`)}
            </button>
          ))}
        </div>
        {/* zoom: fewer columns = bigger crops */}
        <div className="inline-flex items-center gap-1">
          <Button
            variant="outline" size="icon" className="h-7 w-7"
            title={t('data.annotate.zoomIn')}
            disabled={cols <= MIN_COLS}
            onClick={() => setCols(c => Math.max(MIN_COLS, c - 1))}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="w-4 text-center text-xs text-muted-foreground">{cols}</span>
          <Button
            variant="outline" size="icon" className="h-7 w-7"
            title={t('data.annotate.zoomOut')}
            disabled={cols >= MAX_COLS}
            onClick={() => setCols(c => Math.min(MAX_COLS, c + 1))}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>
        {/* source filter: all-labeled / human / AI / unlabeled queue */}
        <div className="inline-flex overflow-hidden rounded-md border text-xs">
          {(['all', 'human', 'ai', 'unlabeled'] as const).map(s => (
            <button
              key={s}
              className={`px-2.5 py-1 ${src === s ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground'}`}
              onClick={() => setSrc(s)}
            >
              {t(`data.annotate.filter.${s}`)} ({srcCounts[s]})
            </button>
          ))}
        </div>
        {/* object filter: narrow to a single table */}
        <Select
          className="h-7 text-xs" wrapperClassName="w-auto"
          value={tableFilter}
          title={t('data.annotate.tableFilter.label')}
          onChange={e => setTableFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
        >
          <option value="all">
            {t('data.annotate.tableFilter.all')} ({tableOptions.reduce((n, o) => n + o.count, 0)})
          </option>
          {tableOptions.map(o => (
            <option key={o.id} value={o.id}>🍽 {o.name} ({o.count})</option>
          ))}
        </Select>
        {BINARY_FIELD[mode] ? (
          <span className="text-xs text-muted-foreground">
            <kbd className="rounded bg-muted px-1">Q</kbd> {t(`data.annotate.bin.${mode}.yes`)} ·{' '}
            <kbd className="rounded bg-muted px-1">W</kbd> {t(`data.annotate.bin.${mode}.no`)} · ←→↑↓
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            {STATE_KEYS.map(s => (
              <span key={s.field} className="mr-2">
                <kbd className="rounded bg-muted px-1 uppercase">{s.key}</kbd> {s.label}
              </span>
            ))}
            · <kbd className="rounded bg-muted px-1">Space</kbd> {t('data.annotate.next')}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-emerald-600">{saved}</span>
          <Tooltip content={t('data.annotate.counterHint')}><span
            className="text-xs text-muted-foreground"
          >
            {t('data.annotate.frame')} {Math.min(focus + 1, visible.length)} / {visible.length}
            {remainingInView !== null && ` · ${t('data.annotate.left')} ${remainingInView}`}
            {mode === 'in_frame' && ` (${t('data.annotate.freeOnly')})`}
            {mode === 'state' && ` (${t('data.annotate.freeInFrameOnly')})`}
          </span></Tooltip>
          <Button
            variant="secondary" size="sm" disabled={submitting || !visible.length}
            onClick={markReviewed}
            title={t('data.annotate.reviewHint')}
          >
            <CheckCheck className="mr-1 h-4 w-4" /> {t('data.annotate.markReviewed')} ({visible.length})
          </Button>
          <Button variant="outline" size="sm" disabled={submitting} onClick={remove}>
            <Trash2 className="mr-1 h-4 w-4" /> {t('data.annotate.deleteLocal')}
          </Button>
          <Button size="sm" disabled={submitting} onClick={upload}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1 h-4 w-4" />}
            {t('data.annotate.upload')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="text-xs text-muted-foreground">{t(`data.annotate.hint.${mode}`)}</p>
        {cropsResp.dataset && (
          <span className="text-xs text-muted-foreground">
            · 📍 {t('data.annotate.point')}:{' '}
            <span className="font-medium text-foreground">
              {cropsResp.dataset.zone_name ?? cropsResp.dataset.name}
            </span>
            {' · '}{t('data.annotate.version')}:{' '}
            <span className="font-mono">
              {cropsResp.dataset.version ?? cropsResp.dataset.version_id.slice(0, 8)}
            </span>
          </span>
        )}
      </div>

      {/* optional note recorded on the new ClearML dataset version */}
      <div className="rounded-md border bg-muted/20 p-3">
        <label htmlFor="ann-desc" className="mb-1 block text-sm font-medium">
          {t('data.annotate.descLabel')}
        </label>
        <p className="mb-1.5 text-xs text-muted-foreground">{t('data.annotate.descHelp')}</p>
        <Textarea
          id="ann-desc"
          value={desc}
          onChange={e => setDesc(e.target.value)}
          rows={2}
          placeholder={t('data.annotate.descPlaceholder')}
          className="resize-y text-sm"
        />
      </div>

      {/* grid — N per row (zoom), fills the page width */}
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {visible.map((it, i) => {
          const lab = labels[keyOf(it)] ?? {};
          const isFocus = i === focus;
          const activeFlags = STATE_KEYS.filter(s => lab[s.field]);
          let outline = 'outline outline-4 outline-slate-300';
          if (mode === 'in_frame') {
            outline = lab.table_in_frame === false
              ? 'outline outline-4 outline-rose-500' : 'outline outline-4 outline-emerald-500';
          } else if (mode === 'presence') {
            if (lab.occupied) outline = 'outline outline-4 outline-emerald-500';
          } else if (activeFlags.length) {
            outline = 'outline outline-4 outline-amber-500';
          }
          return (
            <Tooltip key={keyOf(it)} content={`table ${it.table_id} · frame ${it.frame_id}`}><div
              ref={el => { cardRefs.current[i] = el; }}
              onClick={() => setFocus(i)}
              className={`group relative cursor-pointer rounded-lg bg-white p-1 ${outline} ${
                isFocus
                  ? 'z-10 scale-[1.03] shadow-xl ring-[6px] ring-blue-600 ring-offset-2'
                  : 'opacity-95 hover:opacity-100'
              }`}
            >
              {(() => {
                const s = stateOf(it);
                if (s === 'human') return (
                  <Tooltip content={t('data.annotate.humanBadge')}><span
                    className="absolute right-1.5 top-1.5 z-10 flex items-center gap-0.5 rounded bg-blue-600/90 px-1 py-0.5 text-[9px] font-semibold uppercase text-white"
                  >
                    <CheckCheck className="h-3 w-3" />
                  </span></Tooltip>
                );
                if (s === 'unlabeled') return (
                  <Tooltip content={t('data.annotate.unlabeledBadge')}><span
                    className="absolute right-1.5 top-1.5 z-10 rounded bg-amber-500/90 px-1 py-0.5 text-[9px] font-semibold uppercase text-white"
                  >
                    {t('data.annotate.unlabeledBadge')}
                  </span></Tooltip>
                );
                return (
                  <Tooltip content={t('data.annotate.aiBadge')}><span
                    className="absolute right-1.5 top-1.5 z-10 rounded bg-slate-500/80 px-1 py-0.5 text-[9px] font-semibold uppercase text-white"
                  >
                    {t('data.annotate.aiBadge')}
                  </span></Tooltip>
                );
              })()}
              <Tooltip content={t('data.annotate.deletePhoto')}><button
                className="absolute left-1.5 top-1.5 z-10 rounded bg-rose-600/90 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-rose-700"
                aria-label={t('data.annotate.deletePhoto')}
                onClick={(e) => { e.stopPropagation(); deletePhoto(it); }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button></Tooltip>
              <img
                src={`${API_BASE}${it.crop_url}`}
                loading="lazy"
                alt={`t${it.table_id} f${it.frame_id}`}
                className="block h-auto max-h-[600px] w-full rounded bg-black/5 object-contain"
              />
              <div className="mt-1 flex items-center justify-between gap-1 text-[11px] text-muted-foreground">
                <Tooltip content={it.camera ?? ''}><span className="truncate">
                  📷 {it.camera ?? `cam ${it.camera_id ?? '?'}`}
                </span></Tooltip>
                <span className="flex shrink-0 items-center gap-1">
                  {(() => {
                    const bf = BINARY_FIELD[mode];
                    if (!bf) return null;
                    // Unlabeled (undefined) must NOT read as «свободно» — show a neutral
                    // "not set" chip so a blank crop is visually distinct from a free one.
                    const raw = lab[bf] as boolean | undefined;
                    if (raw === undefined) {
                      return (
                        <span className="rounded bg-amber-100 px-1 font-semibold uppercase text-amber-800">
                          {t('data.annotate.unset')}
                        </span>
                      );
                    }
                    const on = raw;
                    const cls = mode === 'in_frame'
                      ? (on ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800')
                      : (on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700');
                    return (
                      <span className={`rounded px-1 font-semibold uppercase ${cls}`}>
                        {t(`data.annotate.bin.${mode}.${on ? 'yes' : 'no'}`)}
                      </span>
                    );
                  })()}
                  <Tooltip content={it.table}><span className="truncate">🍽 {it.table}</span></Tooltip>
                </span>
              </div>
              {mode === 'state' && (
                <div className="flex flex-wrap gap-0.5">
                  {activeFlags.map(s => (
                    <span key={s.field} className="rounded bg-amber-100 px-1 text-[10px] uppercase text-amber-800">
                      {s.key}
                    </span>
                  ))}
                  {!activeFlags.length && <span className="text-[10px] text-muted-foreground">пусто</span>}
                </div>
              )}
            </div></Tooltip>
          );
        })}
      </div>

      {/* dependency gate: a later model only labels crops the earlier one passed */}
      <Dialog open={gateFor !== null} onOpenChange={(o) => { if (!o) setGateFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{gateFor && t(`data.annotate.gate.${gateFor}.title`)}</DialogTitle>
            <DialogDescription>{gateFor && t(`data.annotate.gate.${gateFor}.body`)}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGateFor(null)}>
              {t('data.annotate.gate.back')}
            </Button>
            <Button onClick={() => {
              if (gateFor) { setAcked(prev => new Set(prev).add(gateFor)); setMode(gateFor); }
              setGateFor(null);
            }}>
              {t('data.annotate.gate.ok')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
